import { PaginationState } from 'app/shared/pagination/pagination.model';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  copyMainProjectDocument,
  copyMainProjectDocuments,
  deleteMainProjectDocument,
  deleteMainProjectDocumentAttachment,
  fetchMainProjectDocumentAttachments,
  fetchMainProjectDocuments,
  getMainProjectData,
  getMainProjectDocument,
  getMainProjectDocumentVariant,
  saveMainProjectData,
  saveMainProjectDocument,
  updateMainProjectDocumentAttachmentStatus,
  uploadMainProjectDocumentFile
} from 'app/edozvola/cases/case-main-project-documents/service/case-main-project-documents.service';
import { showToast } from 'app/shared/util/toast.util';
import { queryClient } from 'app/app';
import {
  MainProjectDataModel,
  MainProjectDocumentModel
} from 'app/edozvola/cases/case-main-project-documents/model/case-main-project-documents.model';
import { toast } from 'react-toastify';
import { FileUploadModel } from 'app/shared/file-upload/model/file-upload.model';
import { messages, TABLE_QUERY_KEYS } from 'app/config/constants';
import { useLocation } from 'react-router-dom';
import success = toast.success;
import error = toast.error;

export const saveMainProjectDataQuery = (idCase: string, resetForm: any) => {
  return useMutation({
    mutationFn: (mainProjectData: MainProjectDataModel) => {
      return saveMainProjectData(idCase, mainProjectData);
    },
    onSuccess: (data, variables, context) => {
      showToast(success, messages.DATA_SAVED);
      resetForm({ values: variables });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchMainProjectDataQuery = (caseUuid: string) => {
  return useQuery({
    queryKey: ['main-project-data', caseUuid],
    queryFn: () => getMainProjectData(caseUuid),
    enabled: !!caseUuid,
  });
};

export const saveMainProjectDocumentQuery = (idCase: string, onSuccessCallback) => {
  return useMutation({
    mutationFn: (mainProjectDocument: MainProjectDocumentModel) => {
      return saveMainProjectDocument(idCase, mainProjectDocument);
    },
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
      onSuccessCallback();
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchMainProjectDocumentsTableQuery = (pagination: PaginationState, uuid: string) => {
  const location = useLocation();
  const caseInPreview = location.pathname.includes('cases-preview');
  return useQuery({
    queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE, { ...pagination }, uuid, { caseInPreview: caseInPreview }],
    queryFn: ({ signal }) => fetchMainProjectDocuments({ signal, pagination, uuid, caseInPreview }),
  });
};

export const deleteMainProjectDocumentQuery = () => {
  return useMutation({
    mutationFn: (idMainProjectDocument: number) => deleteMainProjectDocument(idMainProjectDocument),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchMainProjectDocumentQuery = (idMainProjectDocument: string) => {
  return useQuery({
    queryKey: ['main-project-document', idMainProjectDocument],
    queryFn: () => getMainProjectDocument(idMainProjectDocument),
    enabled: !!idMainProjectDocument,
  });
};

export const copyMainProjectDocumentQuery = () => {
  return useMutation({
    mutationFn: (idMainProjectDocument: number) => copyMainProjectDocument(idMainProjectDocument),
    onSuccess: () => {
      showToast(success, messages.DATA_COPIED);
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const uploadMainProjectDocumentAttachmentQuery = (idMainProjectDocument: string, onSuccessCallback, onErrorCallback) => {
  return useMutation({
    mutationFn: (file: FileUploadModel) => uploadMainProjectDocumentFile(idMainProjectDocument, file),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
      onSuccessCallback(variables);
    },
    onError: (data, variables, context) => onErrorCallback(data, variables),
  });
};

export const fetchMainProjectDocumentAttachmentsQuery = (pagination: PaginationState, idMainProjectDocument: string) => {
  const location = useLocation();
  const caseInPreview = location.pathname.includes('cases-preview');
  return useQuery({
    queryKey: [
      'main-project-document-attachments-table',
      { idMainProjectDocument: idMainProjectDocument, pagination: { ...pagination }, caseInPreview: caseInPreview },
    ],
    queryFn: ({ signal }) =>
      fetchMainProjectDocumentAttachments(
        idMainProjectDocument,
        pagination.page,
        pagination.size,
        pagination.column,
        pagination.direction,
        caseInPreview,
      ),
    enabled: idMainProjectDocument !== null,
  });
};

export const deleteMainProjectDocumentAttachmentQuery = () => {
  return useMutation({
    mutationFn: (idMainProjectDocumentAttachment: number) => deleteMainProjectDocumentAttachment(idMainProjectDocumentAttachment),
    onSuccess: () => {
      showToast(success, messages.DATA_DELETED);
      queryClient.invalidateQueries({ queryKey: ['main-project-document-attachments-table'] });
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const updateMainProjectDocumentAttachmentStatusQuery = () => {
  return useMutation({
    mutationFn: (idMainProjectDocumentAttachment: number) => updateMainProjectDocumentAttachmentStatus(idMainProjectDocumentAttachment),
    onSuccess: () => {
      showToast(success, messages.DATA_SAVED);
      queryClient.invalidateQueries({ queryKey: ['main-project-document-attachments-table'] });
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const copyMainProjectDocumentsFromCaseQuery = (caseUuid: string, onSuccessCallback: any) => {
  return useMutation({
    mutationFn: (caseUuidToCopyFrom: string) => copyMainProjectDocuments(caseUuid, caseUuidToCopyFrom),
    onSuccess: () => {
      onSuccessCallback();
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEYS.MAIN_PROJECT_DOCUMENTS_TABLE] });
      showToast(success, messages.DATA_COPIED);
    },
    onError: (err: any) => {
      showToast(error, err);
    },
  });
};

export const fetchMainProjectDocumentVariantQuery = (idCase: string, caseInPreview: boolean) => {
  return useQuery({
    queryKey: ['main-project-document-variant', { idCase: idCase, caseInPreview: caseInPreview }],
    queryFn: () => getMainProjectDocumentVariant(idCase, caseInPreview),
  });
};
